import { useState, useEffect, useCallback } from 'react';
import { Target, TrendingUp, Zap, RefreshCw, ChevronRight } from 'lucide-react';
import { SeverityBadge } from './SEOOSShared';
import { Button } from '@/components/ui/button';

const API = '';

export default function SEOOSOpportunities() {
  const [ctr, setCtr] = useState(null);
  const [opps, setOpps] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const token = localStorage.getItem('admin_token');
  const headers = { 'Authorization': `Bearer ${token}` };

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API}/api/seoos/ctr`, { headers }).then(r => r.json()),
      fetch(`${API}/api/seoos/opportunities`, { headers }).then(r => r.json()),
    ]).then(([c, o]) => { setCtr(c); setOpps(o); setLoading(false); }).catch(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const analyze = async () => {
    setAnalyzing(true);
    await fetch(`${API}/api/seoos/ctr/analyze`, { method: 'POST', headers });
    await fetch(`${API}/api/seoos/link-graph/rebuild`, { method: 'POST', headers });
    load();
    setAnalyzing(false);
  };

  if (loading) return <div className="text-center py-16 text-slate-500">Yukleniyor...</div>;

  const quick_wins = ctr?.quick_wins || [];
  const link_recs = opps?.link_recommendations || [];
  const boost = opps?.boost_candidates || [];
  const insights = ctr?.ctr_insights || [];

  return (
    <div className="space-y-6" data-testid="seoos-opportunities">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-black text-white" style={{ fontFamily: 'var(--font-heading)' }}>
          <Target className="w-5 h-5 inline mr-2 text-purple-400" />SEO Firsatlari & CTR Analizi
        </h2>
        <Button onClick={analyze} disabled={analyzing} className="bg-purple-600 hover:bg-purple-500 text-white text-xs">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${analyzing ? 'animate-spin' : ''}`} />
          {analyzing ? 'Analiz ediliyor...' : 'Yeniden Analiz Et'}
        </Button>
      </div>

      {/* CTR Stats */}
      {ctr && (
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-purple-950/30 to-slate-900 border border-purple-500/20 rounded-xl p-5 text-center">
            <p className="text-3xl font-black text-purple-400">{ctr.total_pages_analyzed}</p>
            <p className="text-xs text-slate-400 mt-1">Analiz Edilen Sayfa</p>
          </div>
          <div className="bg-slate-900/40 border border-slate-800/50 rounded-xl p-5 text-center">
            <p className="text-3xl font-black text-amber-400">{ctr.avg_title_score}</p>
            <p className="text-xs text-slate-400 mt-1">Ort. Baslik Skoru</p>
          </div>
          <div className="bg-slate-900/40 border border-slate-800/50 rounded-xl p-5 text-center">
            <p className="text-3xl font-black text-emerald-400">%{ctr.avg_estimated_ctr}</p>
            <p className="text-xs text-slate-400 mt-1">Ort. Tahmini CTR</p>
          </div>
        </div>
      )}

      {/* CTR Insights */}
      {insights.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-bold text-white uppercase">CTR Icgoruleri</h3>
          {insights.map((ins, i) => (
            <div key={i} className="bg-slate-900/40 border border-slate-800/50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <SeverityBadge severity={ins.severity} />
                <span className="text-sm font-bold text-white">{ins.title}</span>
              </div>
              <p className="text-xs text-slate-400">{ins.detail}</p>
            </div>
          ))}
        </div>
      )}

      {/* Quick Wins */}
      <div className="bg-slate-900/40 border border-amber-500/20 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-slate-800/30 bg-amber-500/5">
          <h3 className="text-sm font-bold text-amber-400 uppercase"><Zap className="w-4 h-4 inline mr-1" />Hizli Kazanimlar (Quick Wins)</h3>
        </div>
        <div className="divide-y divide-slate-800/20">
          {quick_wins.map((opp, i) => (
            <div key={i} className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded">{opp.opportunity_score}</span>
                  <span className="text-sm font-bold text-white">{opp.page}</span>
                  <span className="text-[10px] text-slate-500">{opp.type}</span>
                </div>
                <span className="text-[10px] text-emerald-400">{opp.expected_impact}</span>
              </div>
              <div className="flex items-center gap-4 text-[10px] text-slate-500 mb-2">
                <span>Baslik: {opp.title_score}/100</span>
                <span>Pozisyon: ~{opp.estimated_position}</span>
                <span>CTR: %{opp.estimated_ctr}</span>
                <span>Gosterim: ~{opp.estimated_impressions}</span>
              </div>
              <div className="text-xs text-slate-600 mb-2 truncate">Mevcut: {opp.current_title}</div>
              <div className="space-y-1">
                {opp.fixes?.map((fix, fi) => (
                  <div key={fi} className="flex items-center gap-2 text-xs text-purple-300">
                    <ChevronRight className="w-3 h-3 text-purple-500 shrink-0" />{fix}
                  </div>
                ))}
              </div>
            </div>
          ))}
          {quick_wins.length === 0 && <div className="p-8 text-center text-slate-500 text-sm">Analiz calistirilmadi. "Yeniden Analiz Et" butonuna basin.</div>}
        </div>
      </div>

      {/* Authority Boost */}
      {boost.length > 0 && (
        <div className="bg-slate-900/40 border border-slate-800/50 rounded-xl p-5">
          <h3 className="text-sm font-bold text-white uppercase mb-3"><TrendingUp className="w-4 h-4 inline mr-1 text-emerald-400" />Otorite Artirilacak Sayfalar</h3>
          <div className="grid grid-cols-2 gap-2">
            {boost.slice(0, 10).map((p, i) => (
              <div key={i} className="flex items-center gap-2 text-xs p-2 bg-slate-800/30 rounded-lg">
                <span className="text-red-400 font-bold w-6 text-right">{p.authority}</span>
                <span className="text-slate-500 w-14 text-[10px]">{p.type}</span>
                <span className="text-slate-300 flex-1 truncate">{p.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
